import { Boxes, Coins, Building2, ClipboardCheck, ClipboardList, Factory, FolderTree, Landmark, LayoutDashboard, Package, PackageCheck,
  ScanLine, Settings, ShieldCheck, ShoppingCart, Truck, Users, Warehouse } from 'lucide-react';
import { PERMISSIONS, terminalLayout, terminalShowsNav } from '@erp/shared';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  permission?: string;
  anyOf?: string[];
  /**
   * Pantalla de puesto de fábrica (el escaneo del almacén): en el menú de oficina sobra, aunque
   * el rol lo tenga todo. Se ofrece en el terminal de ese puesto y a quien tiene ese rol —que
   * puede acabar en una sesión de oficina y necesita llegar igualmente—.
   */
  stationOnly?: boolean;
  /**
   * Pantalla de administración del catálogo: en el menú de oficina y en el de compras sí, pero no
   * como pestaña del puesto de fábrica, donde lo que se hace es escanear y recepcionar.
   *
   * Se decide por el **terminal** (`terminalLayout`), no por el rol ni por `stationVisible`: un
   * almacenero en una sesión de oficina la ve, y en el puesto de fábrica no, igual que el armazón.
   */
  desktopOnly?: boolean;
}

export interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

export const NAV_KEYS: Record<string, string> = {
  '/': 'dashboard', '/customers': 'customers', '/orders': 'orders', '/kits': 'kits',
  '/warehouse': 'warehouse',
  '/packaging': 'packaging', '/logistics': 'logistics', '/products': 'products',
  // Las familias son parte del módulo de artículos: heredan su visibilidad por terminal (almacén y
  // compras las ven donde ven Artículos; un puesto de embalaje no).
  '/families': 'products',
  '/stock': 'stock', '/suppliers': 'suppliers', '/invoices': 'invoices',
  '/purchase-orders': 'purchase-orders', '/models': 'models', '/planning': 'planning', '/admin': 'admin',
  // Tesorería es de oficina: en el armazón de puesto no aparece (no hay caja en fábrica).
  '/treasury': 'treasury',
  // El arancel es de oficina: se consulta al preparar un pedido, no en el puesto de fábrica.
  '/tariff': 'tariff',
  // Las monedas son de oficina: se consultan al comprar o al pagar, no en el puesto de fábrica.
  '/currencies': 'currencies',
  // Los datos de la empresa son de oficina, como la administración: mismo alcance en los puestos.
  '/company': 'admin'
};

export const GROUPS: NavGroup[] = [
  { labelKey: 'nav.operations', items: [
    { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, permission: PERMISSIONS.DASHBOARD_READ },
    { to: '/kits', labelKey: 'nav.kits', icon: Boxes, permission: PERMISSIONS.KITS_READ },
    { to: '/packaging', labelKey: 'nav.packaging', icon: PackageCheck, permission: PERMISSIONS.PACKAGING_READ },
    { to: '/logistics', labelKey: 'nav.logistics', icon: Truck, permission: PERMISSIONS.LOGISTICS_READ }
  ] },
  // Lo que la fábrica vende y lo que la fábrica compra son dos mundos: en el menú van
  // separados y con el nombre de cada uno, para que nadie confunda un pedido con el otro.
  { labelKey: 'nav.sales', items: [
    { to: '/orders', labelKey: 'nav.salesOrders', icon: ShoppingCart, permission: PERMISSIONS.ORDERS_READ },
    { to: '/customers', labelKey: 'nav.customers', icon: Users, permission: PERMISSIONS.ORDERS_READ }
  ] },
  { labelKey: 'nav.warehouse', items: [
    { to: '/warehouse', labelKey: 'nav.warehouseStation', icon: ScanLine, permission: PERMISSIONS.KITS_READ, stationOnly: true },
    { to: '/products', labelKey: 'nav.products', icon: Package, permission: PERMISSIONS.PRODUCTS_READ },
    { to: '/families', labelKey: 'nav.families', icon: FolderTree, permission: PERMISSIONS.PRODUCTS_READ, desktopOnly: true },
    { to: '/stock', labelKey: 'nav.stock', icon: Warehouse, permission: PERMISSIONS.STOCK_READ }
  ] },
  { labelKey: 'nav.purchasing', items: [
    { to: '/purchase-orders', labelKey: 'nav.purchaseOrders', icon: ClipboardCheck, permission: PERMISSIONS.PURCHASES_READ },
    { to: '/suppliers', labelKey: 'nav.suppliers', icon: Factory, permission: PERMISSIONS.SUPPLIERS_READ },
    { to: '/invoices', labelKey: 'nav.invoices', icon: ClipboardList, permission: PERMISSIONS.PURCHASES_READ }
  ] },
  { labelKey: 'nav.engineering', items: [
    { to: '/models', labelKey: 'nav.models', icon: Boxes, permission: PERMISSIONS.MIRRORS_READ }
  ] },
  { labelKey: 'nav.planning', items: [
    { to: '/planning', labelKey: 'nav.planning', icon: ShieldCheck, permission: PERMISSIONS.PLANNING_READ }
  ] },
  { labelKey: 'nav.admin', items: [
    { to: '/admin', labelKey: 'nav.admin', icon: Settings, anyOf: [PERMISSIONS.USERS_MANAGE, PERMISSIONS.AUDIT_READ, PERMISSIONS.SETTINGS_MANAGE] },
    // Monedas, arancel y tesorería no son «compras»: son ajustes de la empresa y viven aquí.
    { to: '/currencies', labelKey: 'nav.currencies', icon: Coins, permission: PERMISSIONS.FINANCE_READ },
    { to: '/company', labelKey: 'nav.company', icon: Building2, permission: PERMISSIONS.FINANCE_READ },
    { to: '/tariff', labelKey: 'nav.tariff', icon: Landmark, permission: PERMISSIONS.PURCHASES_READ },
    { to: '/treasury', labelKey: 'nav.treasury', icon: Landmark, permission: PERMISSIONS.FINANCE_READ, desktopOnly: true }
  ] }
];

/**
 * Si el usuario debe ver las pantallas de puesto en el menú de oficina. Hoy solo el almacén
 * tiene una, pero la regla vive aquí y no dentro del armazón: cuando el embalaje tenga la suya,
 * se añade su rol en este mismo sitio y ya está probado.
 */
export function stationVisibleFor(roles: readonly { code: string }[] | null | undefined): boolean {
  return Boolean(roles?.some((role) => role.code === 'warehouse'));
}

/**
 * Entradas de menú que el usuario puede ver **en este terminal**: permiso del rol y alcance
 * del puesto. Es la única regla de filtrado, compartida por el armazón de escritorio y el de
 * puesto, para que los dos menús no puedan discrepar. La única diferencia deliberada son las
 * pantallas de puesto (`stationOnly`): el menú de oficina no las ofrece salvo a quien tiene el
 * rol de ese puesto.
 */
export function visibleNav(
  can: (permission: string) => boolean,
  terminalKind: string | null | undefined,
  options: { stationVisible?: boolean } = {}
): NavGroup[] {
  const stationVisible = options.stationVisible ?? terminalLayout(terminalKind) === 'station';
  return GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      const permissionOk = item.anyOf
        ? item.anyOf.some((permission) => can(permission))
        : !item.permission || can(item.permission);
      if (!permissionOk) return false;
      // Las pantallas de puesto no se ofrecen desde el armazón de oficina, ni siquiera al
      // administrador. Quien tiene ese rol sí las ve: su sesión puede ser de oficina y necesita
      // llegar al puesto sin escribir la dirección a mano.
      if (item.stationOnly && !stationVisible) return false;
      if (item.desktopOnly && terminalLayout(terminalKind) === 'station') return false;
      return terminalShowsNav(terminalKind, NAV_KEYS[item.to] ?? '');
    })
  })).filter((group) => group.items.length > 0);
}

/** Las entradas como una lista plana, para la barra de pestañas del puesto. */
export function visibleNavItems(
  can: (permission: string) => boolean,
  terminalKind: string | null | undefined,
  options: { stationVisible?: boolean } = {}
): NavItem[] {
  return visibleNav(can, terminalKind, options).flatMap((group) => group.items);
}

/**
 * Pestañas del puesto: sin la raíz, que en un terminal redirige al inicio del puesto y
 * sería un toque muerto en la barra principal.
 */
export function stationNavItems(
  can: (permission: string) => boolean,
  terminalKind: string | null | undefined
): NavItem[] {
  return visibleNavItems(can, terminalKind).filter((item) => item.to !== '/');
}
